import { NetworkStatus, useQuery } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Loader } from '@10xLabs/react-web/components/Loader'
import EmptyState from 'components/v2/EmptyState'
import { NewDriverStep1 } from 'features/Drivers/components/NewDrivers/Step1'
import { GET_DRIVER } from 'features/Drivers/queries-legacy'
import { FormattedMessage } from 'react-intl'
import { Button } from 'components/v2/Button'
import { filterEmptyValue } from 'utils/object'
import { NewDriversStep2 } from 'features/Drivers/components/NewDrivers/Step2'

interface INewDriversProps {
  isEdit?: boolean
}

export const NewDrivers = ({ isEdit }: INewDriversProps): ReactElement => {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const driverId = params.driverId || searchParams.get('driverId') || undefined
  const step = parseInt(searchParams.get('step') || '1')

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_DRIVER, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !driverId,
    variables: {
      input: {
        id: driverId
      }
    }
  })

  useEffect(() => {
    if (driverId) {
      refetch()
    }
  }, [step])

  let content = <></>
  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="partners.edit.error.description"
            defaultMessage="There was an error while loading your partner"
          />
        </EmptyState.Description>
        <EmptyState.Action>
          <Button
            onClick={(): void => {
              refetch()
            }}
            kind="primary"
          >
            <FormattedMessage id="partners.edit.error.action" defaultMessage="Reload your partner" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    switch (step) {
      case 1:
        content = (
          <NewDriverStep1
            driver={data?.tdriver}
            onSubmit={(driverId?: string): void => {
              const params = filterEmptyValue({ driverId, step: '2' })
              setSearchParams(params)
            }}
            onCancel={(): void => navigate('/drivers')}
            isEdit={isEdit || driverId !== undefined}
          />
        )
        break
      case 2:
        content = (
          <NewDriversStep2
            driver={data?.tdriver}
            onSubmit={(): void => navigate('/drivers')}
            onCancel={(): void => {
              const params = filterEmptyValue({
                driverId: isEdit ? undefined : driverId,
                step: '1'
              })
              setSearchParams(params)
            }}
            isEdit={isEdit}
          />
        )
        break
    }
  }

  return content
}
